import type { ProposalsListResponse } from '../core/types';

/**
 * Tombstones for proposals whose approve/dismiss request is IN FLIGHT.
 *
 * The flicker this kills: resolving a proposal optimistically removes its row
 * from the list caches, but that removal is a one-shot cache edit with no
 * memory. Any list refetch during the resolve round-trip — an autosave
 * mutation's onSuccess invalidation, a WS repagination for an unrelated
 * proposal, a chat-task tracker refresh — rebuilds the cache from the server,
 * which still reports the proposal as pending, resurrecting the removed row
 * until the resolution commits and the confirmation path removes it again
 * (disappear → reappear → disappear).
 *
 * Instead of silencing every such trigger individually (whack-a-mole — the
 * refetchType:'none' fix in #12170 silenced only the bulk path's own
 * refetch), the list queryFn filters tombstoned ids out of every response it
 * returns. The tombstone's lifetime is exactly the uncertainty window:
 * added at optimistic removal, cleared per-proposal the moment its request
 * settles — on success the server is authoritative (the row is genuinely
 * gone from pending lists), on failure the caller restores the row and the
 * tombstone must not suppress it.
 *
 * Module-level on purpose (same pattern as the list's scroll anchors): the
 * resolving window must span every list surface and cache reader, not one
 * component instance. Bounded by in-flight resolutions, so it cannot grow.
 */
const resolvingIds = new Set<number>();

export function addResolvingTombstones(ids: Iterable<number>): void {
  for (const id of ids) {
    resolvingIds.add(id);
  }
}

export function clearResolvingTombstones(ids: Iterable<number>): void {
  for (const id of ids) {
    resolvingIds.delete(id);
  }
}

/** Test-only: empty the registry so test files can't leak into each other. */
export function __resetResolvingTombstonesForTesting(): void {
  resolvingIds.clear();
}

/**
 * Drop tombstoned rows from a list response, keeping count and
 * filter_tag_counts consistent with the surviving rows — the same
 * adjustments the optimistic removal applies, so a mid-window refetch
 * reproduces the optimistic state instead of overwriting it.
 * Returns the input untouched when no row is tombstoned (the common case).
 */
export function filterResolvingTombstones(
  response: ProposalsListResponse
): ProposalsListResponse {
  if (resolvingIds.size === 0 || !response?.results) {
    return response;
  }
  const removed = response.results.filter(p => resolvingIds.has(p.id));
  if (removed.length === 0) {
    return response;
  }

  const results = response.results.filter(p => !resolvingIds.has(p.id));
  const filterTagCounts = { ...response.filter_tag_counts };
  for (const proposal of removed) {
    for (const tag of proposal.filter_tags ?? []) {
      if (filterTagCounts[tag] !== undefined) {
        filterTagCounts[tag] = Math.max(0, (filterTagCounts[tag] ?? 0) - 1);
      }
    }
  }

  return {
    ...response,
    results,
    count: Math.max(0, (response.count ?? 0) - removed.length),
    filter_tag_counts: filterTagCounts,
  };
}
